import {
    Component,
    OnInit,
    OnDestroy,
    Input,
    ElementRef
} from '@angular/core';

import cloneDeep from 'lodash/fp/cloneDeep';

import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';

const rescaleResources = [
    'flag',
    'creep-npc',
    'flag-secondary',
    'powerBank',
    'extractor',
    'controller',
    'controller-level',
    'constructedWall',
    'nuker',
    'nuker-border',
    'link',
    'link-border',
    'extension-border50',
    'extension-border100',
    'extension-border200',
    'lab',
    'terminal-border',
    'terminal-arrows',
    'tombstone-border',
    'tombstone-resource',
    'bodyPartBar',
    'tower-base',
    'storage',
    'storage-border'
];

const TICK_DURATION = 3;

@Component({
    selector: 'app-screeps-room',
    template: '',
    styleUrls: ['./room.component.less']
})
export class ScreepsRoomComponent implements OnInit, OnDestroy {
    _destroySbj: Subject<void> = new Subject();

    _gameApp;

    @Input() zoom: number = 16;
    @Input() width: number = 800;
    @Input() height: number = 800;
    @Input() pan: [number, number] = [0, 0];
    @Input() tickDuration: number = TICK_DURATION;
    @Input() worldConfig: any = {};

    _roomSbj: BehaviorSubject<IRoom> = new BehaviorSubject(null);
    @Input() set room(room: IRoom) {
        this._roomSbj.next(room);
    }
    get room(): IRoom {
        return this._roomSbj.getValue();
    }

    get terrain() {
        return decodeTerrain(this.room.terrain, this.room.room);
    }

    _decorationsSbj: BehaviorSubject<any[]> = new BehaviorSubject([]);
    decorations$: Observable<any[]> = this._decorationsSbj.asObservable();
    @Input() set decorations(decorations: any[]) {
        this._decorationsSbj.next(decorations || []);
    }
    get decorations() {
        return this._decorationsSbj.getValue();
    }

    _stateSbj: BehaviorSubject<IGameState> = new BehaviorSubject(null);
    state$: Observable<IGameState> = this._stateSbj.asObservable();
    @Input() set state(state: IGameState) {
        this._stateSbj.next({
            objects: [],
            users: {},
            info: {},
            // TODO: this string crash GameRenderer
            // gameTime: this.gameTime,
            flags: [],
            visual: '',
            ...state,
        });
    }
    get state() {
        return this._stateSbj.getValue();
    }

    constructor(
        private _ref: ElementRef
    ) {
    }

    async ngOnInit() {
        await this._initRenderer();

        this._gameApp.pan(...this.pan);

        this.decorations$
            .pipe(takeUntil(this._destroySbj))
            .pipe(tap((decorations) => this.setDecorations(decorations)))
            .subscribe();

        this.state$
            .pipe(takeUntil(this._destroySbj))
            .pipe(tap((state) => this.applyState(state)))
            .subscribe();
    }

    ngOnDestroy() {
        this._destroySbj.next();
        this._destroySbj.complete();

        this._releaseRenderer();
    }

    async init() {
        return await this._initRenderer();
    }

    // tslint:disable-next-line: member-ordering
    _initializing = null;
    async _initRenderer() {
        if (this._initializing) {
            return this._initializing;
        }

        if (this._gameApp) {
            return this._gameApp;
        }

        this._initializing = new Promise(async (resolve) => {
            // @ts-ignore
            const resourceMap = { ...RENDERER_RESOURCES };
            // @ts-ignore
            const worldConfigs = cloneDeep(this.worldConfig);

            this.decorations.forEach((decorationItem) => {
                if (decorationItem.decoration.type === 'metadata') {
                    Object.assign(resourceMap, decorationItem.decoration.resources);
                    worldConfigs.metadata.objects[decorationItem.decoration.objectType] =
                        decorationItem.decoration.metadata;
                }
            });

            console.debug('ScreepsRoomComponent::initRenderer', 'GameRenderer.compileMetadata');
            // GameRenderer.compileMetadata(worldConfigs.metadata);

            console.debug('ScreepsRoomComponent::initRenderer', 'gameApp::constructor');
            const gameApp = new GameRenderer({
                size: {
                    width: this.width,
                    height: this.height
                },
                resourceMap,
                worldConfigs,
                onGameLoop() {
                },
                rescaleResources,
                countMetrics: false,
                useDefaultLogger: true,
                backgroundColor: 0x0F1012,
            });

            console.debug('ScreepsRoomComponent::initRenderer', 'gameApp::init');
            await gameApp.init(this._ref.nativeElement);
            console.debug('ScreepsRoomComponent::initRenderer', 'gameApp::setTerrain');
            await gameApp.setTerrain(this.terrain);

            gameApp.resize();
            gameApp.zoomLevel = this.zoom / 100;

            this._gameApp = gameApp;

            this._initializing = null;
            resolve(this._gameApp);
        });

        return this._initializing;
    }

    _releaseRenderer() {
        if (!this._gameApp) {
            return;
        }

        this._gameApp.release();

        const canvas = this._ref.nativeElement.querySelector('canvas');
        if (!canvas) {
            console.error('No Canvas');
        } else {
            this._ref.nativeElement.querySelector('canvas').remove();
        }
    }

    setDecorations(decorations: any[]) {
        console.debug('ScreepsRoomComponent::setDecorations', decorations);
        this._gameApp.setDecorations(decorations);
    }

    applyState(state: IGameState) {
        console.debug('ScreepsRoomComponent::applyState', state);
        this._gameApp.applyState(state, this.tickDuration);
    }
}

function decodeTerrain(str, room) {
    const result = [];

    for (let y = 0; y < 50; y++) {
        for (let x = 0; x < 50; x++) {
            const code = str.charAt(y * 50 + x);

            if (code === '1') {
                result.push({room, x, y, type: 'wall'});
            }

            if (code === '2') {
                result.push({room, x, y, type: 'swamp'});
            }
        }
    }

    return result;
}
